import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { BorderRadius, Tokens } from '../Theme';

import {
  CounterSize,
  SizeScheme,
  CounterColor,
  ColorScheme,
  CounterBackgroundColor,
  BackgroundColorScheme,
} from './Counter.types';

const sizeScheme: (px: Theme['px']) => SizeScheme = (px) => ({
  [CounterSize.large]: {
    height: px(20),
  },
  [CounterSize.medium]: {
    height: px(20),
  },
  [CounterSize.small]: {
    height: px(16),
  },
  [CounterSize.extraSmall]: {
    height: px(16),
  },
});

const paddingScheme: Record<
  CounterSize,
  (px: Theme['px'], tokens: Tokens, signed: boolean) => number
> = {
  [CounterSize.large]: (px: Theme['px'], tokens: Tokens, signed: boolean) =>
    signed ? tokens.spacing['4'] : px(7),
  [CounterSize.medium]: (px: Theme['px'], tokens: Tokens, signed: boolean) =>
    signed ? tokens.spacing['4'] : px(7),
  [CounterSize.small]: (px: Theme['px'], tokens: Tokens, signed: boolean) =>
    signed ? tokens.spacing['4'] : px(7),
  [CounterSize.extraSmall]: (px: Theme['px']) => px(5),
};

const colorScheme: ColorScheme = {
  [CounterColor.green]: (p) => p.success.main,
  [CounterColor.orange]: (p) => p.warning.main,
  [CounterColor.red]: (p) => p.error.main,
  [CounterColor.blue]: (p) => p.blue.main,
  [CounterColor.purple]: (p) => p.review.main,
  [CounterColor.black]: (p) => p.text.primary,
  [CounterColor.white]: (p) => p.background.lightPrimary,
};

const backgroundScheme: BackgroundColorScheme = {
  [CounterBackgroundColor.gray]: (p) => p.background.greyscalePrimary,
  [CounterBackgroundColor.green]: (p) => p.success.main,
  [CounterBackgroundColor.lightGreen]: (p) => p.success.light,
  [CounterBackgroundColor.orange]: (p) => p.warning.main,
  [CounterBackgroundColor.lightOrange]: (p) => p.warning.light,
  [CounterBackgroundColor.red]: (p) => p.error.main,
  [CounterBackgroundColor.lightRed]: (p) => p.error.light,
  [CounterBackgroundColor.blue]: (p, t) => t.colors.original.brand.primary,
  [CounterBackgroundColor.lightBlue]: (p) => p.blue.light,
  [CounterBackgroundColor.violet]: (p, tokens) =>
    tokens.colors.accent.text.violet,
  [CounterBackgroundColor.purple]: (p) => p.review.main,
  [CounterBackgroundColor.lightPurple]: (p) => p.review.light,
  [CounterBackgroundColor.white]: (p) => p.background.paper,
};

export const useStyles = makeStyles<{
  backgroundColor: CounterBackgroundColor;
  color?: keyof typeof CounterColor;
  signed: boolean;
  size: CounterSize;
  borderRadius: keyof BorderRadius;
}>()((
  { palette, tokens, px },
  { backgroundColor, color, signed, size, borderRadius },
) => {
  return {
    counter: {
      fontWeight: tokens.typography.variant.caption.fontWeight,
      fontSize: tokens.typography.variant.caption.fontSize,
      lineHeight: tokens.typography.variant.caption.lineHeight,
      borderRadius: tokens.radius[borderRadius],
      color: color ? colorScheme[color](palette) : 'inherit',
      minWidth: sizeScheme(px)[size].height,
      height: sizeScheme(px)[size].height,
      backgroundColor: backgroundScheme[backgroundColor](palette, tokens),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingLeft: paddingScheme[size](px, tokens, signed),
      paddingRight: paddingScheme[size](px, tokens, signed),
      paddingTop: signed ? 0 : tokens.spacing['2'],
      paddingBottom: signed ? 0 : tokens.spacing['2'],
      boxSizing: 'border-box',
    },
  };
});
